import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MDBIcon } from "mdbreact";
import Swal from "sweetalert2";
import {
  axioKit,
  fullName,
  getPhysicianGenderIcon,
} from "../../../../../../../../services/utilities";
import { SetPHYSICIANS } from "../../../../../../../../services/redux/slices/assets/persons/physicians";
import RegisterModal from "../../../../../sources/inhouse/collapse/modal";
import "../../../../../../../../components/searchables/physicians/style.css";

// every word of the key must be found in the label
const matches = (label = "", key = "") => {
  const text = String(label).toUpperCase();
  return key
    .toUpperCase()
    .split(/[\s,]+/)
    .filter(Boolean)
    .every((word) => text.includes(word));
};

const physicianName = (physician = {}) => fullName(physician?.user?.fullName);

// "Lastname, Firstname Middlename" or "Firstname Lastname"
const toFullName = (name = "") => {
  const [before = "", after = ""] = name.split(",").map((part) => part.trim());
  if (after) return { lname: before, fname: after, mname: "" };
  const words = before.split(/\s+/).filter(Boolean);
  if (words.length < 2) return { fname: before, mname: "", lname: "" };
  return {
    fname: words.slice(0, -1).join(" "),
    mname: "",
    lname: words[words.length - 1],
  };
};

/**
 * Physician picker of the POS.
 * Lists the physicians tagged to the selected source (or to your branch when
 * there is no source), then searches the roster (tag them to the source), and
 * lastly registers a new physician thru the "add physician" modal.
 */
export default function PickSourcePhysician({
  tagged = [],
  branchId = "", // branch the physician will be tagged to
  sourceName = "",
  disabled,
  formSubmitted,
  isSuccess,
  onChange = () => {},
  onTagged = () => {},
}) {
  const { token } = useSelector(({ auth }) => auth),
    { collections: allPhysicians = [] } = useSelector(
      ({ physicians }) => physicians,
    ),
    [query, setQuery] = useState(""),
    [selected, setSelected] = useState(null), // picked physician
    [register, setRegister] = useState(null), // register modal, null when closed
    [isFocused, setIsFocused] = useState(false),
    [roster, setRoster] = useState(null), // null: roster not searched yet
    [isSearching, setIsSearching] = useState(false),
    [isSaving, setIsSaving] = useState(false),
    onChangeRef = useRef(onChange),
    dispatch = useDispatch();

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // a new source has its own physicians, and a successful checkout clears it
  const isDone = isSuccess && !formSubmitted;
  const [resetKey, setResetKey] = useState(0);
  useEffect(() => {
    if (isDone) setResetKey((key) => key + 1);
  }, [isDone]);
  useEffect(() => {
    setQuery("");
    setSelected(null);
    setRoster(null);
  }, [branchId, resetKey]);

  // what is sent on checkout: the picked user, or a plain name
  useEffect(() => {
    if (selected) return onChangeRef.current({ _id: selected.user._id });
    onChangeRef.current({ _id: query.trim() });
  }, [selected, query]);

  const key = query.trim(),
    isTyping = !!key && !selected,
    target = sourceName || "your branch",
    shown = isTyping
      ? tagged.filter((physician) => matches(physicianName(physician), key))
      : tagged;

  const handleChange = ({ target }) => {
    setQuery(target.value);
    setSelected(null);
    setRoster(null);
  };

  const handlePick = (physician) => {
    setQuery(physicianName(physician));
    setSelected(physician);
    setRoster(null);
    setIsFocused(false);
  };

  // one entry per physician, skipping ghosts and the ones already tagged
  const fromRoster = (list = []) => {
    const taggedUsers = tagged.map(({ user }) => String(user?._id));
    const seen = new Set();
    return list.filter((physician) => {
      const userId = String(physician?.user?._id || "");
      if (!physician?.user?._id || seen.has(userId)) return false;
      if (taggedUsers.includes(userId)) return false;
      seen.add(userId);
      return matches(physicianName(physician), key);
    });
  };

  const searchRoster = async () => {
    const found = fromRoster(allPhysicians);
    if (found.length) return setRoster(found);

    // the cached roster may be outdated, fetch it again before giving up
    setIsSearching(true);
    try {
      const { payload = [] } = await axioKit.universal(
        "assets/persons/physicians/browse",
        token,
      );
      localStorage.setItem("physicians", JSON.stringify(payload));
      dispatch(SetPHYSICIANS(payload));
      setRoster(fromRoster(payload));
    } catch (error) {
      setRoster([]);
      Swal.fire({ icon: "error", title: "Search failed", text: error.message });
    } finally {
      setIsSearching(false);
    }
  };

  const handleTag = async (physician) => {
    const name = physicianName(physician);
    const { isConfirmed } = await Swal.fire({
      title: name,
      text: `Tag this physician to ${target}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, tag it!",
    });
    if (!isConfirmed) return;

    setIsSaving(true);
    try {
      await axioKit.update(
        "assets/branches",
        { physicianId: physician._id, branchId },
        token,
        "tagPhysician/update",
      );
      onTagged(physician);
      handlePick(physician);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Tagging failed", text: error.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegister = () => {
    setIsFocused(false);
    setRegister({ fullName: toFullName(key), isRegister: true });
  };

  const handleRegistered = (physician) => {
    if (!physician?.user?._id) return;
    onTagged(physician);
    handlePick(physician);
  };

  const showList = isFocused && !disabled && !isSaving;

  return (
    <div className="physicians-search-container">
      <div className="d-flex align-items-center">
        <input
          type="text"
          className="physicians-search-input form-control form-control-sm"
          placeholder="Search physician..."
          value={query}
          disabled={disabled || isSaving}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && isTyping) {
              e.preventDefault();
              searchRoster();
            }
          }}
        />
        {isSaving && <MDBIcon icon="spinner" pulse className="ml-2" />}
      </div>
      {showList && (
        <ul className="physicians-results-list" style={{ zIndex: 10 }}>
          {!isTyping && !tagged.length && (
            <li className="p-2 text-center text-muted small">
              No physician tagged to {target} yet. Type a name to search the
              roster.
            </li>
          )}

          {shown.length > 0 && (
            <>
              <li className="px-2 pt-1 text-muted small">
                Tagged to {target}
              </li>
              {shown.map((physician) => (
                <li
                  key={physician._id}
                  className="physicians-result-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePick(physician)}
                >
                  <div className="physicians-result-content">
                    {getPhysicianGenderIcon(physician?.user?.isMale)}
                    {physicianName(physician)}
                  </div>
                </li>
              ))}
            </>
          )}

          {isTyping && !shown.length && (
            <li className="p-2 text-center text-muted small">
              🚫 Not tagged to {target}.
            </li>
          )}

          {isTyping &&
            (roster === null ? (
              <li
                className="physicians-result-item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={searchRoster}
              >
                <div className="physicians-result-content text-primary">
                  <MDBIcon
                    icon={isSearching ? "spinner" : "search"}
                    pulse={isSearching}
                    className="mr-2"
                  />
                  Search "{key}" in roster
                </div>
              </li>
            ) : (
              <>
                <li className="px-2 pt-1 text-muted small">
                  Roster{roster.length ? ", click to tag" : ""}
                </li>
                {roster.length ? (
                  roster.map((physician) => (
                    <li
                      key={physician._id}
                      className="physicians-result-item"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleTag(physician)}
                    >
                      <div className="physicians-result-content">
                        {getPhysicianGenderIcon(physician?.user?.isMale)}
                        {physicianName(physician)}
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="p-2 text-center text-muted small">
                    🚫 "{key}" is not in the roster either.
                  </li>
                )}
                <li
                  className="physicians-result-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleRegister}
                >
                  <div className="physicians-result-content text-primary">
                    <MDBIcon icon="user-plus" className="mr-2" />
                    Register "{key}" as a new physician
                  </div>
                </li>
              </>
            ))}
        </ul>
      )}

      <RegisterModal
        show={!!register}
        toggle={() => setRegister(null)}
        selected={register || {}}
        branch={{ _id: branchId, name: target }}
        onTagged={handleRegistered}
      />
    </div>
  );
}
